/**
 * Coach's Journal - diary-style log of posts.
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDateUK } from '../lib/utils';
import { Plus, Search, BookOpen, Edit2, Trash2, Calendar, Smile, Tag, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().split('T')[0];
const blank = { title: '', body: '', entry_date: todayISO(), mood: '', tags: '' };

export default function Journal() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/journal`);
      setEntries(res.data);
    } catch {
      toast.error('Failed to load journal');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ ...blank, entry_date: todayISO() });
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title || '',
      body: e.body || '',
      entry_date: e.entry_date || todayISO(),
      mood: e.mood || '',
      tags: (e.tags || []).join(', ')
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.body.trim()) {
      toast.error('Write something in the body');
      return;
    }
    const payload = {
      title: form.title || null,
      body: form.body,
      entry_date: form.entry_date || todayISO(),
      mood: form.mood || null,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : []
    };
    try {
      if (editing) {
        const res = await axios.patch(`${API}/journal/${editing.id}`, payload);
        setEntries(prev => {
          const next = prev.map(e => e.id === res.data.id ? res.data : e);
          return sortEntries(next);
        });
        toast.success('Entry updated');
      } else {
        const res = await axios.post(`${API}/journal`, payload);
        setEntries(prev => sortEntries([res.data, ...prev]));
        toast.success('Entry added');
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const remove = async (e) => {
    if (!window.confirm('Delete this entry? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/journal/${e.id}`);
      setEntries(prev => prev.filter(x => x.id !== e.id));
      toast.success('Entry deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(e =>
      (e.title || '').toLowerCase().includes(q) ||
      (e.body || '').toLowerCase().includes(q) ||
      (e.mood || '').toLowerCase().includes(q) ||
      (e.tags || []).some(t => t.toLowerCase().includes(q))
    );
  }, [entries, search]);

  const grouped = useMemo(() => groupByMonth(filtered), [filtered]);

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="journal-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">Journal</h1>
            <p className="text-sm text-gray-500 mt-1">A running diary of sessions, observations, and reflections.</p>
          </div>
          <Button onClick={openAdd} className="btn-primary rounded-xl" data-testid="journal-add-btn">
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />New Entry
          </Button>
        </div>

        {/* Search */}
        <div className="relative" data-testid="journal-controls">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search entries, moods, tags…"
            className="pl-9 rounded-xl"
            data-testid="journal-search"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              data-testid="journal-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Entries */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="text-center py-12">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" strokeWidth={1.5} />
              <p className="text-gray-500">
                {entries.length === 0 ? 'No entries yet. Start your first one!' : 'No entries match your search.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8" data-testid="journal-list">
            {grouped.map(([monthLabel, items]) => (
              <section key={monthLabel}>
                <h2 className="text-sm uppercase tracking-wider font-semibold text-gray-500 mb-3 pl-1">{monthLabel}</h2>
                <div className="flex flex-col gap-3">
                  {items.map(entry => <JournalCard key={entry.id} entry={entry} onEdit={openEdit} onDelete={remove} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="journal-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Entry' : 'New Entry'}</DialogTitle>
            <DialogDescription>Capture thoughts, sessions, or reflections.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="journal-date-input" />
              </div>
              <div>
                <Label>Mood (optional)</Label>
                <Input value={form.mood} onChange={(e) => setForm({ ...form, mood: e.target.value })} placeholder="e.g. 😀 or Upbeat" data-testid="journal-mood-input" />
              </div>
            </div>
            <div>
              <Label>Title (optional)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Pre-match team talk" data-testid="journal-title-input" />
            </div>
            <div>
              <Label>Entry</Label>
              <Textarea rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="What happened today?" data-testid="journal-body-input" />
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="training, tactics, injuries" data-testid="journal-tags-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="journal-cancel-btn">Cancel</Button>
              <Button onClick={save} disabled={!form.body.trim()} className="btn-primary" data-testid="journal-save-btn">
                {editing ? 'Save Changes' : 'Add Entry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function JournalCard({ entry, onEdit, onDelete }) {
  return (
    <article
      className="bg-white rounded-2xl p-4 sm:p-5 border border-gray-200 hover:shadow-sm transition-shadow"
      data-testid={`journal-entry-${entry.id}`}
    >
      <header className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" strokeWidth={1.5} />
              {formatDateUK(entry.entry_date)}
            </span>
            {entry.mood && (
              <span className="inline-flex items-center gap-1">
                <Smile className="w-3 h-3" strokeWidth={1.5} />
                {entry.mood}
              </span>
            )}
          </div>
          {entry.title && <h3 className="mt-1 font-semibold text-gray-900 text-lg leading-tight">{entry.title}</h3>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={() => onEdit(entry)} className="text-gray-500 hover:text-gray-700 rounded-lg" data-testid={`journal-edit-btn-${entry.id}`} title="Edit">
            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(entry)} className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`journal-delete-btn-${entry.id}`} title="Delete">
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>
      <p className="mt-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{entry.body}</p>
      {entry.tags && entry.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {entry.tags.map(t => (
            <span key={t} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
              <Tag className="w-2.5 h-2.5" strokeWidth={2} />{t}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

function sortEntries(list) {
  return [...list].sort((a, b) => {
    const byDate = (b.entry_date || '').localeCompare(a.entry_date || '');
    if (byDate !== 0) return byDate;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });
}

function groupByMonth(entries) {
  const map = new Map();
  for (const e of entries) {
    const d = e.entry_date ? new Date(e.entry_date + 'T00:00:00') : new Date();
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    if (!map.has(key)) map.set(key, { label, items: [] });
    map.get(key).items.push(e);
  }
  // Already sorted by entry_date desc upstream, but ensure consistent ordering of groups
  return [...map.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([, v]) => [v.label, v.items]);
}
